import { TIMEOUTS } from '@/config/targets'

/**
 * A cross-origin request we are not allowed to read still tells us what we need. The browser
 * resolves it as an opaque response only after TCP and TLS completed, so a resolve means the
 * firewall let us through to port 443. A blocked port gives a network error or hits the timeout.
 */
async function reaches(host: string, timeoutMs: number): Promise<boolean> {
	const controller = new AbortController()
	const timer = setTimeout(() => controller.abort(), timeoutMs)

	try {
		// A fresh query string per attempt keeps proxies and the browser cache out of the result.
		await fetch(`https://${host}/?fwcheck=${nonce()}`, {
			mode: 'no-cors',
			cache: 'no-store',
			credentials: 'omit',
			redirect: 'follow',
			signal: controller.signal
		})
		return true
	} catch {
		return false
	} finally {
		clearTimeout(timer)
	}
}

/** One retry on a longer timeout, so a slow link is not reported as a blocked port. */
export async function checkTcpTarget(host: string): Promise<boolean> {
	if (await reaches(host, TIMEOUTS.tcpFirstTry)) return true
	return reaches(host, TIMEOUTS.tcpRetry)
}

function nonce(): string {
	if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
	return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}
